import React, { useState, useEffect } from 'react';
import dayjs from 'dayjs';
import { Modal, Form, Input, Select, Alert, DatePicker, message } from 'antd';
import apiService, { admin } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const { Option } = Select;

export default function VehiclePlateModal({ visible, onCancel, onSuccess, onCloseUsageSuccess, branches = [], mode = 'register', usageToClose = null }) {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const { user, romaneioData } = useAuth();
  const [userBranchLabel, setUserBranchLabel] = useState('');
  const [effectiveBranchId, setEffectiveBranchId] = useState(undefined);

  // Garantir que a filial exibida seja sempre a do usuário logado e com rótulo correto
  useEffect(() => {
    const resolveUserBranch = async () => {
      try {
        // 1) Tente obter pelo objeto assignedBranch
        const assigned = user?.assignedBranch;
        if (assigned?.id) {
          setEffectiveBranchId(assigned.id);
          const name = assigned.name || assigned.descricao;
          const sigla = assigned.sigla || assigned.acronym;
          setUserBranchLabel(`${name || 'Filial'}${sigla ? ` (${sigla})` : ''}`);
          return;
        }

        // 2) Tente pelo assignedBranchId
        if (user?.assignedBranchId) {
          const branchId = user.assignedBranchId;
          setEffectiveBranchId(branchId);
          // Buscar label via lista de filiais
          const list = await admin.getBranches();
          const found = Array.isArray(list) ? list.find(b => b.id === branchId) : null;
          if (found) {
            setUserBranchLabel(`${found.name || 'Filial'}${found.sigla ? ` (${found.sigla})` : ''}`);
          } else {
            setUserBranchLabel(`Filial ID ${branchId}`);
          }
          return;
        }

        // 3) Tente por branchId legado
        if (user?.branchId) {
          const branchId = user.branchId;
          setEffectiveBranchId(branchId);
          const list = await admin.getBranches();
          const found = Array.isArray(list) ? list.find(b => b.id === branchId) : null;
          if (found) {
            setUserBranchLabel(`${found.name || 'Filial'}${found.sigla ? ` (${found.sigla})` : ''}`);
          } else {
            setUserBranchLabel(`Filial ID ${branchId}`);
          }
          return;
        }

        // 4) Tente por empresa/código
        if (user?.empresa) {
          const codigo = String(user.empresa);
          const list = await admin.getBranches();
          const found = Array.isArray(list) ? list.find(b => String(b.codigo) === codigo) : null;
          if (found) {
            setEffectiveBranchId(found.id);
            setUserBranchLabel(`${found.name || 'Filial'}${found.sigla ? ` (${found.sigla})` : ''}`);
            return;
          }
        }

        // 5) Login por romaneio: a unidade/sigla validada no ERP vira a filial
        if (user?.romaneio && user?.empresa) {
          const sigla = String(user.empresa).toUpperCase().trim();
          if (sigla) {
            setEffectiveBranchId(sigla);
            setUserBranchLabel(sigla);
            return;
          }
        }

        // Fallback final
        setEffectiveBranchId(undefined);
        setUserBranchLabel('Filial não definida');
      } catch {
        setUserBranchLabel(
          user?.romaneio && user?.empresa
            ? String(user.empresa).toUpperCase().trim()
            : 'Filial do usuário',
        );
      }
    };

    resolveUserBranch();
  }, [user]);

  // Sincronizar o valor do campo branchId no formulário quando a filial efetiva for resolvida
  useEffect(() => {
    if (!visible) return;
    if (effectiveBranchId !== undefined && effectiveBranchId !== null) {
      form.setFieldsValue({ branchId: effectiveBranchId });
    } else {
      form.resetFields(['branchId']);
    }
  }, [visible, effectiveBranchId]);

  // Login por romaneio: pré-preenche a placa com a placa validada no ERP (ssw0146)
  useEffect(() => {
    if (!visible || mode !== 'register' || !user?.romaneio) return;
    const placa = String(romaneioData?.placa || user?.codigo || user?.usuario || '')
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 8);
    if (placa) form.setFieldsValue({ placa });
  }, [visible, mode, romaneioData, user?.romaneio, user?.codigo, user?.usuario, form]);

  const handleOk = async () => {
    try {
      const values = await form.validateFields();
      setLoading(true);

      if (mode === 'close' && usageToClose?.id) {
        const id = usageToClose.id;
        try {
          await apiService.veiculoUso.update(id, { encerrar: true });
        } catch (err) {
          const msg = err?.message || '';
          const normalized = String(msg).toLowerCase();
          const isNotFound = normalized.includes('404') || normalized.includes('not found') || normalized.includes('registro de uso não encontrado');
          if (isNotFound) {
            const placaLookup = usageToClose?.placa || usageToClose?.veiculo?.placa;
            let diaStr;
            try {
              const src = usageToClose?.dtUso || usageToClose?.dtInicio;
              if (src) {
                const d = new Date(src);
                const toLocalYYYYMMDD = (date) => {
                  try {
                    const y = date.getFullYear();
                    const m = String(date.getMonth() + 1).padStart(2, '0');
                    const da = String(date.getDate()).padStart(2, '0');
                    return `${y}-${m}-${da}`;
                  } catch {
                    return '';
                  }
                };
                if (!isNaN(d.getTime())) diaStr = toLocalYYYYMMDD(d);
              }
            } catch { }

            try {
              const list = await apiService.veiculoUso.list({ placa: placaLookup, ...(diaStr ? { dia: diaStr } : {}) });
              let remoteUso = null;
              if (Array.isArray(list)) {
                remoteUso = list.find(u => !u.dtFim) || list[0];
              }
              if (!remoteUso?.id) {
                throw new Error('Uso do veículo não encontrado no servidor para esta placa/dia.');
              }
              const remoteId = remoteUso.id;
              await apiService.veiculoUso.update(remoteId, { encerrar: true });
              message.info('Registro remoto resolvido; atualização aplicada.');
            } catch (resolveErr) {
              throw resolveErr;
            }
          } else {
            throw err;
          }
        }

        message.success('Uso do veículo encerrado com sucesso.');
        onCloseUsageSuccess?.();
        form.resetFields();
      } else {
        const placa = values.placa.trim().toUpperCase();
        const v = await apiService.veiculo.getByPlaca(placa);

        const notFoundMsg = typeof v?.message === 'string' && v.message.toLowerCase().includes('não encontrado');
        const exists = !!v?.id && !notFoundMsg && String(v?.placa || '').toUpperCase() === placa;

        if (!exists) {
          const serverMsg = typeof v?.message === 'string' ? v.message : 'Veículo não encontrado. Cadastre o veículo antes de vincular.';
          message.error(serverMsg);
          setLoading(false);
          return;
        }

        const hoje = new Date();
        hoje.setHours(0, 0, 0, 0);
        const toLocalYYYYMMDD = (date) => {
          try {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
          } catch {
            return '';
          }
        };
        const diaStr = toLocalYYYYMMDD(hoje);
        const usuarioCodigo = user?.codigo;
        let filialSigla = v?.branch?.sigla || null;
        if (!filialSigla) {
          try {
            const list = await admin.getBranches();
            const found = Array.isArray(list) ? list.find(b => b.id === v?.branchId) : null;
            filialSigla = found?.sigla || null;
          } catch { }
        }
        if (!filialSigla && values?.branchId) {
          const found = Array.isArray(branches) ? branches.find(b => (b.id ?? b.codigo ?? b.value) === values.branchId) : null;
          filialSigla = found?.sigla || null;
        }
        if (!filialSigla && user?.romaneio && user?.empresa) {
          filialSigla = String(user.empresa).toUpperCase().trim() || null;
        }

        // Criar uso do veículo e retornar o próprio uso para o caller
        let uso = null;
        if (!filialSigla) {
          message.error('Não foi possível determinar a filial do veículo. Verifique a configuração da filial e tente novamente.');
          setLoading(false);
          return;
        }

        try {
          uso = await apiService.veiculoUso.create({ placa, dia: diaStr, usuarioCodigo, filial: filialSigla });
        } catch (createErr) {
          const msg = String(createErr?.message || '').toLowerCase();
          const shouldNotExistFilial = msg.includes('should not exist') && msg.includes('filial');
          const filialObrigatoria = msg.includes('filial é obrigatória');
          if (shouldNotExistFilial) {
            try {
              uso = await apiService.veiculoUso.create({ placa, dia: diaStr, usuarioCodigo });
            } catch (retryErr) {
              console.warn('Criação sem filial também falhou:', retryErr?.message);
            }
          } else if (filialObrigatoria) {
            message.error('Filial deve ser informada de acordo com a configuração do veículo.');
          }
          if (!uso?.id) {
            console.warn('Falha ao obter uso criado, tentando localizar por placa/dia:', createErr?.message);
          }
        }
        if (!uso?.id) {
          try {
            const list = await apiService.veiculoUso.list({ placa, dia: diaStr, usuarioCodigo });
            const usosArr = Array.isArray(list?.data)
              ? list.data
              : Array.isArray(list?.items)
                ? list.items
                : Array.isArray(list)
                  ? list
                  : [];
            uso = usosArr.find(u => !u.dtFim) || usosArr[0] || null;
          } catch { }
        }
        if (!uso?.id) {
          message.error('Não foi possível registrar o uso do veículo.');
          setLoading(false);
          return;
        }

        message.success('Veículo vinculado para hoje.');
        onSuccess?.(uso);
        form.resetFields();
      }
    } catch (err) {
      console.error(err);
      const errorMessage = err?.message || 'Erro ao vincular veículo';
      message.error(errorMessage);
      if (errorMessage === 'Uso do veículo não encontrado no servidor para esta placa/dia.') {
        onCancel?.();
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={mode === 'close' ? 'Encerrar uso do veículo' : 'Informar placa do veículo'}
      open={visible}
      onCancel={async () => {
        const src = usageToClose?.dtUso || usageToClose?.dtInicio;
        if (mode === 'close' && src) {
          const toSpYYYYMMDD = (date) => {
            try {
              const d = new Date(date);
              const parts = new Intl.DateTimeFormat('sv-SE', {
                timeZone: 'America/Sao_Paulo',
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
              }).formatToParts(d);
              const get = (t) => parts.find((p) => p.type === t)?.value || '00';
              return `${get('year')}-${get('month')}-${get('day')}`;
            } catch {
              return '';
            }
          };
          const todaySp = toSpYYYYMMDD(new Date());
          const usoSp = toSpYYYYMMDD(src);
          if (todaySp !== '' && usoSp !== '' && todaySp !== usoSp) {
            await handleOk();
            return;
          }
        }
        form.resetFields();
        onCancel?.();
      }}
      onOk={handleOk}
      confirmLoading={loading}
      okText={mode === 'close' ? 'Encerrar uso' : 'Confirmar'}
      cancelText="Cancelar"
      destroyOnHidden
    >
      <Form form={form} layout="vertical">
        {mode === 'close' && usageToClose ? (
          <>
            <Alert
              type="warning"
              showIcon
              message="Encerrar Coletas do Veículo"
              description={`Placa: ${usageToClose?.placa || usageToClose?.veiculo?.placa || ''} `}
              style={{ marginBottom: 12 }}
            />

          </>
        ) : (
          <>
            <Form.Item
              name="placa"
              label="Placa"
              rules={[
                { required: true, message: 'Informe a placa' },
                { min: 7, max: 8 },
                { pattern: /^[A-Za-z0-9]+$/, message: 'Use apenas letras e números' }
              ]}
            >
              <Input
                placeholder=""
                maxLength={8}
                allowClear
                onChange={(e) => {
                  const raw = e.target.value || '';
                  const hasInvalid = /[^A-Za-z0-9]/.test(raw);
                  const sanitized = raw.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
                  if (hasInvalid) {
                    message.warning('A placa deve conter apenas letras e números');
                  }
                  form.setFieldsValue({ placa: sanitized });
                }}
                onBlur={() => {
                  const v = form.getFieldValue('placa') || '';
                  form.setFieldsValue({ placa: v.trim().toUpperCase() });
                }}
              />
            </Form.Item>

            <Form.Item
              name="branchId"
              label="Filial"
            // Campo não obrigatório: se vazio, usa a filial do usuário
            >
              <Select
                allowClear
                placeholder={userBranchLabel || 'Selecione uma filial (opcional)'}
              >
                {effectiveBranchId != null && (
                  <Option key={String(effectiveBranchId)} value={effectiveBranchId}>
                    {userBranchLabel || 'Filial do usuário'}
                  </Option>
                )}
                {Array.isArray(branches) && branches.map((b) => (
                  <Option key={String(b.id ?? b.codigo ?? b.value)} value={b.id ?? b.codigo ?? b.value}>
                    {(b.name || b.descricao || b.label || 'Filial') + (b.sigla ? ` (${b.sigla})` : '')}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </>
        )}
      </Form>
    </Modal>
  );
}